(() => {
  if (customElements.get('customily-cart-preview')) return;

  class CustomilyCartPreview extends HTMLElement {
    connectedCallback() {
      if (this.dataset.previewBound === 'true') return;
      this.dataset.previewBound = 'true';

      this.dialog = this.querySelector('[data-preview-dialog]');
      this.openButtons = [...this.querySelectorAll('[data-preview-open]')];
      this.closeButton = this.querySelector('[data-preview-close]');
      this.image = this.querySelector('[data-preview-image]');
      this.stage = this.querySelector('[data-preview-stage]');
      this.instructions = this.querySelector('[data-preview-instructions]');
      this.error = this.querySelector('[data-preview-error]');
      this.retry = this.querySelector('[data-preview-retry]');
      this.zoomIn = this.querySelector('[data-preview-zoom-in]');
      this.zoomOut = this.querySelector('[data-preview-zoom-out]');
      this.zoomLevel = this.querySelector('[data-preview-zoom-level]');
      this.zoomAnnouncement = this.querySelector('[data-preview-zoom-announcement]');
      this.resetButton = this.querySelector('[data-preview-reset]');
      this.source = this.dataset.previewSrc || this.image?.getAttribute('src') || '';
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.minScale = 1;
      this.maxScale = 2;
      this.pointers = new Map();
      this.dragStart = null;
      this.pinchStart = null;
      this.dragging = false;
      this.gestureHadMultiTouch = false;
      this.transformFrame = 0;
      this.zoomAnnouncementTimer = 0;
      this.zoomAnnouncementPendingPercent = null;

      if (this.image) this.image.draggable = false;
      if (this.stage) {
        this.stage.tabIndex = 0;
        this.stage.setAttribute('role', 'region');
      }

      this.openButtons.forEach(button => button.addEventListener('click', () => this.open(button)));
      this.closeButton?.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('click', event => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog?.addEventListener('close', () => this.resetView());
      this.image?.addEventListener('load', () => this.showImage());
      this.image?.addEventListener('error', () => this.showError());
      this.retry?.addEventListener('click', () => this.retryImage());
      this.zoomIn?.addEventListener('click', () => this.zoomAt(this.scale + 0.5));
      this.zoomOut?.addEventListener('click', () => this.zoomAt(this.scale - 0.5));
      this.resetButton?.addEventListener('click', () => this.resetView());
      this.stage?.addEventListener('pointerdown', event => this.onPointerDown(event));
      this.stage?.addEventListener('pointermove', event => this.onPointerMove(event));
      this.stage?.addEventListener('pointerup', event => this.onPointerEnd(event));
      this.stage?.addEventListener('pointercancel', event => this.onPointerEnd(event, true));
      this.stage?.addEventListener('dblclick', event => event.preventDefault());
      this.stage?.addEventListener('keydown', event => this.onKeyDown(event));
      this.resizeObserver = new ResizeObserver(() => this.requestTransform());
      if (this.stage) this.resizeObserver.observe(this.stage);

      if (this.image?.complete) {
        this.image.naturalWidth > 0 ? this.showImage() : this.showError();
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.transformFrame) window.cancelAnimationFrame(this.transformFrame);
      if (this.zoomAnnouncementTimer) window.clearTimeout(this.zoomAnnouncementTimer);
      this.zoomAnnouncementPendingPercent = null;
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || null;
      this.dialog.showModal();
      this.resetView();
      this.closeButton?.focus();
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      this.opener?.focus();
    }

    stagePoint(clientX, clientY) {
      const rect = this.stage.getBoundingClientRect();
      return {
        x: clientX - rect.left - rect.width / 2,
        y: clientY - rect.top - rect.height / 2
      };
    }

    pointInsideImage(clientX, clientY) {
      if (!this.image) return false;
      const rect = this.image.getBoundingClientRect();
      return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    }

    cycleZoomAt(clientX, clientY) {
      if (this.scale < 1.25) {
        this.zoomAt(1.5, clientX, clientY);
      } else if (this.scale < 1.75) {
        this.zoomAt(2, clientX, clientY);
      } else {
        this.resetView();
      }
    }

    zoomAt(nextScale, clientX, clientY) {
      if (!this.stage || !this.image) return;
      const boundedScale = Math.min(this.maxScale, Math.max(this.minScale, nextScale));
      const rect = this.stage.getBoundingClientRect();
      const focusX = Number.isFinite(clientX) ? clientX : rect.left + rect.width / 2;
      const focusY = Number.isFinite(clientY) ? clientY : rect.top + rect.height / 2;
      const point = this.stagePoint(focusX, focusY);
      const ratio = boundedScale / this.scale;
      this.translateX = point.x - (point.x - this.translateX) * ratio;
      this.translateY = point.y - (point.y - this.translateY) * ratio;
      this.scale = boundedScale;
      this.requestTransform();
    }

    clampTranslation() {
      if (!this.stage || !this.image) return;
      const stageWidth = this.stage.clientWidth;
      const stageHeight = this.stage.clientHeight;
      const baseWidth = this.image.offsetWidth;
      const baseHeight = this.image.offsetHeight;
      const maxX = Math.max(0, (baseWidth * this.scale - stageWidth) / 2);
      const maxY = Math.max(0, (baseHeight * this.scale - stageHeight) / 2);
      this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX));
      this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY));
      if (this.scale === this.minScale) {
        this.translateX = 0;
        this.translateY = 0;
      }
    }

    requestTransform() {
      if (this.transformFrame) return;
      this.transformFrame = window.requestAnimationFrame(() => {
        this.transformFrame = 0;
        this.applyTransform();
      });
    }

    applyTransform() {
      if (!this.image) return;
      this.clampTranslation();
      const transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
      if (this.image.style.transform !== transform) this.image.style.transform = transform;
      const percent = Math.round(this.scale * 100);
      if (this.stage) {
        const zoomed = this.scale > this.minScale ? 'true' : 'false';
        const panning = this.dragging ? 'true' : 'false';
        const pinching = this.pinchStart ? 'true' : 'false';
        if (this.stage.dataset.zoomed !== zoomed) this.stage.dataset.zoomed = zoomed;
        if (this.stage.dataset.panning !== panning) this.stage.dataset.panning = panning;
        if (this.stage.dataset.pinching !== pinching) this.stage.dataset.pinching = pinching;
        if (this.stage.dataset.zoomPercent !== String(percent)) this.stage.dataset.zoomPercent = String(percent);
        const label = this.scale > this.minScale
          ? `Design preview at ${percent} percent. Move the image with a pointer, touch, or the arrow keys; use the zoom controls or keyboard shortcuts to change zoom.`
          : 'Design preview at fit. Use the image surface, zoom controls, or keyboard shortcuts to zoom.';
        if (this.stage.getAttribute('aria-label') !== label) this.stage.setAttribute('aria-label', label);
      }
      if (this.zoomLevel && this.zoomLevel.textContent !== `${percent}%`) this.zoomLevel.textContent = `${percent}%`;
      this.scheduleZoomAnnouncement(percent);
      if (this.zoomOut && this.zoomOut.disabled !== (this.scale <= this.minScale)) this.zoomOut.disabled = this.scale <= this.minScale;
      if (this.zoomIn && this.zoomIn.disabled !== (this.scale >= this.maxScale)) this.zoomIn.disabled = this.scale >= this.maxScale;
      const resetDisabled = this.scale === this.minScale && this.translateX === 0 && this.translateY === 0;
      if (this.resetButton && this.resetButton.disabled !== resetDisabled) this.resetButton.disabled = resetDisabled;
    }

    scheduleZoomAnnouncement(percent) {
      if (!this.zoomAnnouncement || this.zoomAnnouncement.textContent === `Zoom ${percent} percent` || this.zoomAnnouncementPendingPercent === percent) return;
      this.zoomAnnouncementPendingPercent = percent;
      if (this.zoomAnnouncementTimer) window.clearTimeout(this.zoomAnnouncementTimer);
      this.zoomAnnouncementTimer = window.setTimeout(() => {
        this.zoomAnnouncementTimer = 0;
        this.zoomAnnouncementPendingPercent = null;
        this.zoomAnnouncement.textContent = `Zoom ${percent} percent`;
      }, 300);
    }

    resetView() {
      this.scale = this.minScale;
      this.translateX = 0;
      this.translateY = 0;
      this.pointers.clear();
      this.dragStart = null;
      this.pinchStart = null;
      this.dragging = false;
      this.gestureHadMultiTouch = false;
      this.requestTransform();
    }

    onPointerDown(event) {
      if (!this.dialog?.open || event.button > 0) return;
      const pointer = {
        x: event.clientX,
        y: event.clientY,
        startX: event.clientX,
        startY: event.clientY,
        type: event.pointerType,
        startedAt: performance.now(),
        startedOnImage: this.pointInsideImage(event.clientX, event.clientY)
      };
      this.pointers.set(event.pointerId, pointer);

      if (this.pointers.size === 2) {
        this.gestureHadMultiTouch = true;
        for (const pointerId of this.pointers.keys()) this.stage.setPointerCapture?.(pointerId);
        const [a, b] = [...this.pointers.values()];
        const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const point = this.stagePoint(midpoint.x, midpoint.y);
        this.pinchStart = {
          distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
          scale: this.scale,
          contentX: (point.x - this.translateX) / this.scale,
          contentY: (point.y - this.translateY) / this.scale
        };
        this.dragStart = null;
        this.dragging = false;
        if (this.stage.dataset.pinching !== 'true') this.stage.dataset.pinching = 'true';
        event.preventDefault();
      } else {
        this.dragStart = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          translateX: this.translateX,
          translateY: this.translateY,
          startedAt: performance.now(),
          startedOnImage: pointer.startedOnImage
        };
        if (this.scale > this.minScale) {
          this.stage.setPointerCapture?.(event.pointerId);
          event.preventDefault();
        }
      }
      this.requestTransform();
    }

    onPointerMove(event) {
      if (!this.dialog?.open || !this.pointers.has(event.pointerId)) return;
      const prior = this.pointers.get(event.pointerId);
      const samples = event.getCoalescedEvents?.() || [];
      const pointEvent = samples[samples.length - 1] || event;
      this.pointers.set(event.pointerId, { ...prior, x: pointEvent.clientX, y: pointEvent.clientY });

      if (this.pointers.size >= 2 && this.pinchStart) {
        event.preventDefault();
        const [a, b] = [...this.pointers.values()];
        const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const point = this.stagePoint(midpoint.x, midpoint.y);
        const distance = Math.hypot(a.x - b.x, a.y - b.y) || 1;
        this.scale = Math.min(this.maxScale, Math.max(this.minScale, this.pinchStart.scale * distance / this.pinchStart.distance));
        this.translateX = point.x - this.pinchStart.contentX * this.scale;
        this.translateY = point.y - this.pinchStart.contentY * this.scale;
        this.requestTransform();
        return;
      }

      if (this.pointers.size === 1 && this.dragStart?.pointerId === event.pointerId && this.scale > this.minScale) {
        const movement = Math.hypot(pointEvent.clientX - this.dragStart.x, pointEvent.clientY - this.dragStart.y);
        if (movement > 5) this.dragging = true;
        if (!this.dragging) return;
        event.preventDefault();
        if (this.stage.dataset.panning !== 'true') this.stage.dataset.panning = 'true';
        this.translateX = this.dragStart.translateX + pointEvent.clientX - this.dragStart.x;
        this.translateY = this.dragStart.translateY + pointEvent.clientY - this.dragStart.y;
        this.requestTransform();
      }
    }

    onPointerEnd(event, cancelled = false) {
      const pointer = this.pointers.get(event.pointerId);
      const hadMultiple = this.gestureHadMultiTouch || this.pointers.size > 1;
      const movement = pointer ? Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY) : Infinity;
      const duration = pointer ? performance.now() - pointer.startedAt : Infinity;
      const threshold = pointer?.type === 'touch' ? 12 : 5;
      const wasDragging = this.dragging;

      this.pointers.delete(event.pointerId);
      if (this.stage.hasPointerCapture?.(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);

      if (!cancelled && pointer?.startedOnImage && !hadMultiple && !wasDragging && movement <= threshold && duration < 500) {
        this.cycleZoomAt(event.clientX, event.clientY);
      }

      this.pinchStart = null;
      this.dragStart = null;
      this.dragging = false;
      if (this.pointers.size === 1) {
        const [pointerId, remaining] = [...this.pointers.entries()][0];
        this.dragStart = {
          pointerId,
          x: remaining.x,
          y: remaining.y,
          translateX: this.translateX,
          translateY: this.translateY,
          startedAt: performance.now(),
          startedOnImage: remaining.startedOnImage
        };
      } else if (this.pointers.size === 0) {
        this.gestureHadMultiTouch = false;
      }
      this.requestTransform();
      if (this.pointers.size === 0) {
        requestAnimationFrame(() => this.stage?.dispatchEvent(new CustomEvent('gs:zoom-pan-idle', { bubbles: true })));
      }
    }

    onKeyDown(event) {
      const key = event.key;
      if (key === '+' || key === '=') {
        event.preventDefault();
        this.zoomAt(this.scale + 0.5);
      } else if (key === '-') {
        event.preventDefault();
        this.zoomAt(this.scale - 0.5);
      } else if (key === '0' || key === 'Home') {
        event.preventDefault();
        this.resetView();
      } else if (this.scale > this.minScale && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) {
        event.preventDefault();
        const step = 48;
        if (key === 'ArrowLeft') this.translateX += step;
        if (key === 'ArrowRight') this.translateX -= step;
        if (key === 'ArrowUp') this.translateY += step;
        if (key === 'ArrowDown') this.translateY -= step;
        this.requestTransform();
      }
    }

    setControlsDisabled(disabled) {
      [this.zoomIn, this.zoomOut, this.resetButton].forEach(button => {
        if (button) button.disabled = disabled;
      });
    }

    showImage() {
      // This component owns readiness for its generated preview. The theme-wide
      // lazy-image helper can add `.lazy` after this image is already decoded,
      // leaving a valid preview at opacity: 0 until its delayed cleanup runs.
      // Remove that presentation-only state as soon as the real image is ready.
      if (this.image) {
        this.image.classList.remove('lazy', 'lazyload', 'lazyloading');
        this.image.removeAttribute('loading');
      }
      if (this.stage) this.stage.hidden = false;
      if (this.error) this.error.hidden = true;
      this.setControlsDisabled(false);
      this.requestTransform();
    }

    showError() {
      if (this.stage) this.stage.hidden = true;
      if (this.error) this.error.hidden = false;
      this.setControlsDisabled(true);
    }

    retryImage() {
      if (!this.image || !this.source) return;
      if (this.error) this.error.hidden = true;
      this.image.removeAttribute('src');
      window.requestAnimationFrame(() => this.image.setAttribute('src', this.source));
    }
  }

  customElements.define('customily-cart-preview', CustomilyCartPreview);
})();
